import React, { ChangeEvent, LegacyRef, useCallback, useRef, useState } from "react";

import { Tooltip } from "@mui/material";
import ShipeeziNewGrid, {
  Column,
  GeneralActions,
  RowActionsExtra,
} from "Components/AppGrid/shipeezi-new-grid";
import { colors } from "@zapalacorp/tokens";
import MilitaryBadge from "Components/Generic/Icons/MilitaryBadge";
import { format } from "date-fns";
import { StarSlash } from "iconsax-react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { CertificationType } from "Util/enums";

import BadgeStatus from "../../../../../../../../../../components/Generic/BadgeStatus";
import IconAttachSquare from "../../../../../../../../../../components/Generic/Icons/AttachSquare";
import IconDocumentUpload from "../../../../../../../../../../components/Generic/Icons/DocumentUpload";
import IconLike from "../../../../../../../../../../components/Generic/Icons/Like";
import { LoadingSpinner } from "../../../../../../../../../../components/LoadingModal/styles";
import { MemberCertificationStatus } from "../../../../../../../../../../models/member";
import {
  File as CertificationFile,
  GeneralFile,
  MemberCertification,
} from "../../../../../../../../../../models/members/member.certification";
import { Filters, Searches } from "../../../../../../../../../../models/shipeezi.table";
import { downloadAndGeneratePdf } from "../../../../../../../../../../util/downloadAndGeneratePdf";
import { NewRecordButton } from "../../NewRecordButton";
import ManageCertification, { ManageCertificationHandles } from "./ManageCertification";
import {
  ArchiveBadge,
  ClickableRowActionButton,
  ColumnStatus,
  ContentWrapper,
  ExpireLabel,
  UploadButtonInput,
  useStyles,
} from "./styles";
import { useCertificationTabActions } from "./useActions";

type Props = {
  memberId: number;
};

export const MAX_SIZE = 2 * 1024 * 1024;

const CertificationsTab: React.FC<Props> = ({ memberId }) => {
  const { t: translate } = useTranslation();
  const { location } = useHistory<{ isView?: boolean }>();
  const classes = useStyles();

  const manageCertificationRef = useRef<ManageCertificationHandles>(null);
  const fileInput: LegacyRef<HTMLInputElement> = useRef(null);

  const [filters, setFilters] = useState<Filters>({} as Filters);
  const [search, setSearch] = useState<Searches>({} as Searches);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);

  const {
    getCertifications,
    getCertificationsCount,
    useManageCertification,
    useApproveCertification,
  } = useCertificationTabActions(memberId);

  const {
    data: certifications,
    isFetching,
    refetch,
  } = getCertifications(page, pageSize, search, filters);

  const { data: certificationCount, refetch: refetchCount } = getCertificationsCount(
    filters,
    search
  );

  const {
    mutate: onManageCertification,
    variables: variablesManage,
    isLoading,
  } = useManageCertification(() => {
    manageCertificationRef.current?.close();
    refetch();
    refetchCount();
  });

  const {
    mutate: onApproveCertification,
    variables,
    isLoading: isApproving,
  } = useApproveCertification(() => {
    refetch();
    refetchCount();
  });

  const columns: Column[] = [
    {
      name: "description",
      title: translate("description"),
    },
    {
      name: "type",
      title: translate("certificationType"),
      formatComponent: (value: CertificationType) => {
        function handleTypeOfCertificate(typeOfCertificate: CertificationType) {
          switch (typeOfCertificate) {
            case CertificationType.MILITARY:
              return (
                <ContentWrapper>
                  {CertificationType.MILITARY} <MilitaryBadge height="20" width="20" />
                </ContentWrapper>
              );

            case CertificationType.VIP:
              return (
                <ContentWrapper>
                  {CertificationType.VIP}
                  <StarSlash
                    size={24}
                    color={colors.warning500}
                    fill={colors.white}
                    variant="Bold"
                  />
                </ContentWrapper>
              );
            case CertificationType.STANDARD:
            default:
              return <ContentWrapper>{CertificationType.STANDARD}</ContentWrapper>;
          }
        }

        return handleTypeOfCertificate(value);
      },
      className: classes.column,
    },
    {
      name: "url",
      title: translate("url").toUpperCase(),
      formatText: (value) => value || "-",
    },
    {
      name: "reviewDate",
      title: translate("review"),
      hideSearch: true,
      formatComponent: (value) => {
        if (!value) return "-";

        const isExpired = new Date(value).getTime() < new Date().getTime();

        return (
          <ExpireLabel expired={isExpired}>{format(new Date(value), "dd/MM/yyyy")}</ExpireLabel>
        );
      },
    },
    {
      name: "expireDate",
      title: translate("expiry"),
      hideSearch: true,
      formatComponent: (value) => {
        if (!value) return "-";

        const isExpired = new Date(value).getTime() < new Date().getTime();

        return (
          <ExpireLabel expired={isExpired}>{format(new Date(value), "dd/MM/yyyy")}</ExpireLabel>
        );
      },
    },
    {
      name: "generalFile",
      title: translate("file"),
      hideSearch: true,
      formatComponent: (value?: GeneralFile) => {
        if (!value) return "-";

        return (
          <ColumnStatus>
            <ArchiveBadge
              onClick={() => (value.link ? downloadAndGeneratePdf(value.link) : undefined)}
            >
              <IconAttachSquare height={18} width={18} color={colors.gray600} />
              <span>
                {value.originalFilename}.{value.filename?.split(".")[1]}
              </span>
            </ArchiveBadge>
          </ColumnStatus>
        );
      },
    },
    {
      name: "status",
      title: translate("status"),
      hideSearch: true,
      formatComponent: (_, row?: MemberCertification) => {
        if (!row) return "-";

        const backgroundColor =
          row?.status === MemberCertificationStatus.APPROVED || row?.isApproved
            ? colors.success50
            : colors.warning50;
        const color =
          row?.status === MemberCertificationStatus.APPROVED || row?.isApproved
            ? colors.success500
            : colors.warning500;

        return (
          <ColumnStatus>
            <BadgeStatus
              value={row?.status}
              useExternalColor
              backgroundColor={backgroundColor}
              textColor={color}
            />
          </ColumnStatus>
        );
      },
    },
  ];

  const [certificationReupload, setCertificationReupload] = useState<MemberCertification | null>(
    null
  );

  const chooseFile = (data: MemberCertification) => {
    if (fileInput && fileInput?.current) {
      setCertificationReupload(data);
      fileInput?.current?.click();
    }
  };

  const handleSearch = (values: Searches) => {
    setSearch(values);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    const filtersKeys = Object.getOwnPropertyNames(value);

    setFilters(filtersKeys && filtersKeys?.length ? value : ({} as Filters));
  };

  const onUploadFile = (data: MemberCertification | null, event: ChangeEvent<HTMLInputElement>) => {
    if (data && event?.target && event?.target?.files && event?.target?.files?.length > 0) {
      const file = event?.target?.files[0];

      onAddCertification(data, file);
    }
    event.target.value = "";
  };

  const onApproveFile = (data: MemberCertification) => {
    if (data?.id) {
      onApproveCertification({ id: data?.id });
    }
  };

  const onAddCertification = (data: MemberCertification, file: File | null) => {
    const execute = (archive: CertificationFile | null) => {
      const dataToSend: MemberCertification = {
        ...data,
        reviewDate: data?.reviewDate,
        expireDate: data?.expireDate,
        file: archive,
        memberId,
      };

      onManageCertification(dataToSend);
    };

    if (file) {
      const reader = new FileReader();

      reader.addEventListener("load", () => {
        if (reader.result) {
          const fileParts = file.name.split(".");

          execute({
            filename: fileParts[0],
            data: {
              type: fileParts[fileParts.length - 1] || "pdf",
              data: reader.result as any,
            },
          });
        }
      });
      reader.readAsDataURL(file);
    } else {
      execute(null);
    }
  };

  const onOpenNewCertificationPopup = useCallback(() => manageCertificationRef.current?.open(), []);

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
      right: ["handleChangeStatus", "newRecord"],
    },
    handleSearch: {
      onSearch: handleSearch,
    },
    handleFilterBy: {
      categories: [
        {
          name: translate("status"),
          column: "status",
          options: [
            { label: translate("approved"), value: "approved" },
            { label: translate("waitingApproval"), value: "waiting approval" },
          ],
        },
      ],
      onChange: handleFilter,
    },
    newRecord: location?.state?.isView ? undefined : (
      <NewRecordButton
        label={translate("newCertification")}
        onClick={onOpenNewCertificationPopup}
      />
    ),
  };

  const rowActionsExtra: RowActionsExtra[] = [
    {
      title: "Upload",
      buttonAndAction(rowId, row: MemberCertification) {
        if (row?.isApproved || row.status === "Approved") return null;

        if (variablesManage?.id === Number(row?.id) && isLoading) {
          return <LoadingSpinner size={20} />;
        }

        return (
          <Tooltip title={translate("uploadANewFile")}>
            <ClickableRowActionButton disabled={location?.state?.isView}>
              <div onClick={() => (location?.state?.isView ? undefined : chooseFile(row))}>
                <IconDocumentUpload height={20} width={20} color={colors.gray600} />
              </div>
            </ClickableRowActionButton>
          </Tooltip>
        );
      },
    },
    {
      title: "Approve",
      buttonAndAction(_, row) {
        if (row?.isApproved || row.status === "Approved") return null;

        if (variables?.id === Number(row?.id) && isApproving) {
          return <LoadingSpinner size={20} />;
        }

        return (
          <Tooltip title={translate("confirmThisCertificationIsValidated")}>
            <ClickableRowActionButton disabled={location?.state?.isView}>
              <div onClick={() => (location?.state?.isView ? undefined : onApproveFile(row))}>
                <IconLike height={20} width={20} color={colors.gray600} />
              </div>
            </ClickableRowActionButton>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <>
      <UploadButtonInput
        type="file"
        ref={fileInput}
        value=""
        accept=".pdf"
        onChange={(event) =>
          location?.state?.isView ? undefined : onUploadFile(certificationReupload, event)
        }
      />

      <ShipeeziNewGrid
        loadingData={isFetching}
        data={certifications || []}
        totalItems={certificationCount || 0}
        columns={columns}
        showSelection={false}
        showSort={false}
        currentPage={page}
        pageSizeList={[10, 20, 40, 60]}
        onChangePageSize={setPageSize}
        onNextPage={setPage}
        onPreviousPage={setPage}
        onGoToPage={setPage}
        positionActionsColumn="right"
        generalActions={generalActions}
        keyExtractor={(data) => data?.id?.toString() || data?.randomUuid}
        rowActionsExtra={rowActionsExtra}
        emptyData={translate("noInformationToBeShown")}
      />

      <ManageCertification
        ref={manageCertificationRef}
        isLoading={isLoading}
        onAddNewCertification={onAddCertification}
      />
    </>
  );
};

export { CertificationsTab };
